import { Dispatch, PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Disease, DiseaseListItem, GetDiseasesParams, PaginatedResponse, Pagination, typicodeApi } from 'shared/api';

type TDiseaseState = {
  list: Disease[];
  diseaseList: DiseaseListItem[];
  pagination: Pagination | null;
};

const initialState: TDiseaseState = {
  list: [],
  diseaseList: [],
  pagination: null,
};

export const diseaseModel = createSlice({
  name: 'diseases',
  initialState,
  reducers: {
    setDiseases: (state, { payload }: PayloadAction<Disease[]>) => {
      state.list = payload;
    },
    setDiseaseList: (state, { payload }: PayloadAction<DiseaseListItem[]>) => {
      state.diseaseList = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
    addDiseases: (state, { payload }: PayloadAction<Disease>) => {
      state.list = [...state.list, payload];
    },
    updateDiseases: (state, { payload }: PayloadAction<Disease>) => {
      state.list = state.list.map((item) => (item._id === payload._id ? payload : item));
    },
  },
});

// react-query actions (everything that async)
export const DISEASES_ALL_QUERY_KEY = 'diseases-all';
const DISEASE_BY_ID_QUERY_KEY = 'disease-by-id';

export const getDiseasesAllAsync = (params: GetDiseasesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<DiseaseListItem>>>(
    [DISEASES_ALL_QUERY_KEY, params],
    () => typicodeApi.disease.getDiseasesAll(params),
    {
      onSuccess: ({ data }) => {
        dispatch(diseaseModel.actions.setDiseaseList(data.data));
        dispatch(diseaseModel.actions.setPagination(data.pagination));
      },
      onError: () => {
        toast.error('There was an error loading the list of diseases, try reloading the page!');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

export const getDiseaseByIdAsync = (id: string, enabled: boolean = true) =>
  useQuery<AxiosResponse<Disease>>([DISEASE_BY_ID_QUERY_KEY, id], () => typicodeApi.disease.getDiseaseById(id), {
    enabled,
    staleTime: 0, // Данные сразу считаются устаревшими - всегда свежие при открытии
    cacheTime: 0, // Не кешировать - каждый раз загружать заново
    refetchOnWindowFocus: false,
  });

export const postNewDiseaseAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(typicodeApi.disease.postDisease, {
    onSuccess: () => {
      toast.success('Disease added successfully!');
      void queryClient.invalidateQueries(DISEASES_ALL_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error when adding a new disease. Try again!');
    },
  });
};

export const updateDiseaseAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(typicodeApi.disease.patchDisease, {
    onSuccess: () => {
      toast.success('The disease has been successfully changed!');
      void queryClient.invalidateQueries(DISEASES_ALL_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error when changing the disease. Try again!');
    },
  });
};

// selectors
export const useDiseases = () => useSelector((state: RootState) => state.diseases);
export const useDiseaseList = () =>
  useSelector((state: RootState) => ({
    diseaseList: state.diseases.diseaseList,
    pagination: state.diseases.pagination,
  }));
export const isDiseasesEmpty = (): boolean =>
  useSelector(
    createSelector(
      (state: RootState) => state.diseases.list,
      (diseases) => diseases.length === 0,
    ),
  );
export const isDiseaseListEmpty = (): boolean =>
  useSelector(
    createSelector(
      (state: RootState) => state.diseases.diseaseList,
      (diseases) => diseases.length === 0,
    ),
  );
export const reducer = diseaseModel.reducer;
